import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Clock } from 'lucide-react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from '@/components/feedback/toast-store'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Switch } from '@/components/ui/Switch'
import { shiftsQuery } from '@/features/academic/api'
import { useSavePeriod } from '../api'
import { periodSchema, type PeriodFormValues } from '../schemas'
import type { Period } from '../types'

const FIELDS = ['name', 'startTime', 'endTime', 'isBreak', 'shiftId']

/** Create / edit a bell-schedule period. Mounted only while open. */
export function PeriodDialog({
  period,
  onOpenChange,
}: {
  period?: Period
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation('timetable')
  const save = useSavePeriod()
  const shifts = useQuery(shiftsQuery())
  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<PeriodFormValues>({
    resolver: zodResolver(periodSchema),
    defaultValues: {
      name: period?.name ?? '',
      startTime: period?.startTime ?? '',
      endTime: period?.endTime ?? '',
      isBreak: period?.isBreak ?? false,
      shiftId: period?.shift ? String(period.shift.id) : '',
    },
  })

  const onSubmit = handleSubmit(async (v) => {
    try {
      await save.mutateAsync({
        id: period?.id,
        name: v.name,
        startTime: v.startTime,
        endTime: v.endTime,
        isBreak: v.isBreak,
        shiftId: v.shiftId ? Number(v.shiftId) : null,
      })
      toast.success(period ? t('Period updated') : t('Period added'), v.name)
      onOpenChange(false)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="sm"
      icon={<Clock />}
      title={period ? t('Edit period') : t('New period')}
      dismissible={!isSubmitting}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            {t('Cancel')}
          </Button>
          <Button type="submit" form="timetable-period-form" loading={isSubmitting}>
            {t('Save')}
          </Button>
        </>
      }
    >
      <form id="timetable-period-form" onSubmit={onSubmit} noValidate className="space-y-4">
        <FormField label={t('Name')} error={errors.name?.message} required>
          <Input {...register('name')} autoFocus placeholder={t('e.g. Period 1')} maxLength={50} />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label={t('Starts at')} error={errors.startTime?.message} required>
            <Input type="time" {...register('startTime')} />
          </FormField>
          <FormField label={t('Ends at')} error={errors.endTime?.message} required>
            <Input type="time" {...register('endTime')} />
          </FormField>
        </div>
        <FormField
          label={t('Shift')}
          error={errors.shiftId?.message}
          hint={t('Limit the period to sections of one shift, or leave it for every section.')}
        >
          <Select
            options={(shifts.data ?? []).map((s) => ({ value: s.id, label: s.name }))}
            placeholder={t('All shifts')}
            {...register('shiftId')}
          />
        </FormField>
        <div className="flex items-start justify-between gap-4 rounded-control border border-line p-3">
          <div>
            <p id="tt-period-break-label" className="text-sm font-medium text-ink">
              {t('Break')}
            </p>
            <p id="tt-period-break-hint" className="text-xs text-muted">
              {t('Breaks appear on the timetable but never hold lessons.')}
            </p>
          </div>
          <Controller
            control={control}
            name="isBreak"
            render={({ field }) => (
              <Switch
                aria-labelledby="tt-period-break-label"
                aria-describedby="tt-period-break-hint"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            )}
          />
        </div>
      </form>
    </Dialog>
  )
}
